import store from '@/store';
import axios from 'axios';
import { API_BASE_URL, AUTH_TOKEN_STORAGE_KEY, AUTH_USER_STORAGE_KEY, SHOP_ID_STORAGE_KEY } from '@/config/app';
import { extractApiErrorMessage } from '@/support/apiErrors';
import { showAppToast } from '@/support/toastBus';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const headerValue = (headers: any, name: string): unknown => {
  if (!headers) return undefined;
  if (typeof headers.get === 'function') return headers.get(name);

  const lowerName = name.toLowerCase();
  const matchingKey = Object.keys(headers).find((key) => key.toLowerCase() === lowerName);
  return matchingKey ? headers[matchingKey] : undefined;
};

const storedAuthUser = (): any | null => {
  try {
    const raw = localStorage.getItem(AUTH_USER_STORAGE_KEY);
    if (!raw) return null;

    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const accessibleShopIds = (user: any): number[] => {
  if (!Array.isArray(user?.accessible_shops)) return [];

  return user.accessible_shops
    .map((shop: any) => Number(shop?.id || 0))
    .filter((id: number) => Number.isFinite(id) && id > 0);
};

/**
 * Détermine le contexte de navigation à transmettre à l'API. L'identifiant
 * conservé dans le navigateur reste modifiable : le serveur doit vérifier
 * les droits sur la boutique et sur chaque ressource demandée.
 */
const currentRequestShopId = (): number | string | null => {
  const currentShopId = store.getters['shop/currentShopId'] || localStorage.getItem(SHOP_ID_STORAGE_KEY);

  if (currentShopId) {
    return currentShopId;
  }

  const user = store.getters['auth/currentUser'] || storedAuthUser();
  const accessibleIds = accessibleShopIds(user);

  if (accessibleIds.length === 1) {
    return accessibleIds[0];
  }

  if (user?.role === 'caissier' && accessibleIds.length > 1) {
    return null;
  }

  return user?.shop_id || null;
};

// Ajoute automatiquement le token Sanctum dans le header Authorization
api.interceptors.request.use((config: any) => {
  const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY) || store.getters['auth/token'];
  const currentShopId = currentRequestShopId();

  config.headers = config.headers || {};
  config.__suite241HadAuthToken = Boolean(token);

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Intercepteur requête : injecter X-Shop-Id sur toutes les requêtes si un site courant existe.
  const hasExplicitShopId = Boolean(headerValue(config.headers, 'X-Shop-Id'));
  if (currentShopId && !hasExplicitShopId) {
    config.headers['X-Shop-Id'] = String(currentShopId);
  }

  return config;
});

const isMutationMethod = (method?: string): boolean =>
  ['post', 'put', 'patch', 'delete'].includes(String(method || '').toLowerCase());

type ToastAwareRequestConfig = {
  skipErrorToast?: boolean;
  skipSuccessToast?: boolean;
};

const CASHIER_SITE_CHOICE_REQUIRED_KEY = 'suite241.cashierSiteChoiceRequired';
const CASH_REGISTER_SESSION_CLOSED_EVENT = 'suite241:cash-register-session-closed';
const CASH_REGISTER_SESSION_CLOSED_HANDLED_FLAG = '__suite241CashRegisterSessionClosedHandled';
let isRedirectingAfterUnauthenticated = false;
let isHandlingClosedCashRegisterSession = false;

const isLoginRequest = (config?: { url?: string }): boolean => {
  const url = String(config?.url || '');

  return url.includes('/auth/login');
};

const requestHadAuthToken = (config?: { headers?: any; __suite241HadAuthToken?: boolean }): boolean => {
  if (config?.__suite241HadAuthToken) {
    return true;
  }

  return String(headerValue(config?.headers, 'Authorization') || '')
    .toLowerCase()
    .startsWith('bearer ');
};

const isUnauthenticatedError = (error: any): boolean => {
  const status = Number(error?.response?.status || 0);
  const message = extractApiErrorMessage(error, '');

  return status === 401 || message.toLowerCase().includes('unauthenticated');
};

const clearAuthenticatedSession = async (): Promise<void> => {
  store.commit('auth/CLEAR_AUTH');
  window.localStorage.removeItem(CASHIER_SITE_CHOICE_REQUIRED_KEY);

  await Promise.all([
    store.dispatch('shop/clearShopContext'),
    store.dispatch('cashRegister/clear'),
    store.dispatch('userSettings/clear'),
  ]);
};

const redirectToLogin = async (): Promise<void> => {
  try {
    const { default: router } = await import('@/router');

    if (router.currentRoute.value.name !== 'Login') {
      await router.push({ name: 'Login' });
    }
  } catch {
    if (typeof window !== 'undefined' && window.location.pathname !== '/backoffice/login') {
      window.location.assign('/backoffice/login');
    }
  }
};

const handleUnauthenticatedResponse = async (): Promise<void> => {
  await clearAuthenticatedSession();

  if (isRedirectingAfterUnauthenticated) {
    return;
  }

  isRedirectingAfterUnauthenticated = true;

  showAppToast({
    type: 'warning',
    message: 'Session expirée. Veuillez vous reconnecter.',
    duration: 6500,
  });

  try {
    await redirectToLogin();
  } finally {
    isRedirectingAfterUnauthenticated = false;
  }
};

const currentAuthUser = (): any | null => store.getters['auth/currentUser'] || storedAuthUser();

const currentCashRegisterSession = (): any | null => store.getters['cashRegister/currentSession'] || null;

const cashRegisterSessionErrorMessages = (error: any): string[] => {
  const errors = error?.response?.data?.errors;
  const rawMessages = errors?.cash_register_session;

  if (Array.isArray(rawMessages)) {
    return rawMessages.map((message) => String(message || ''));
  }

  if (typeof rawMessages === 'string') {
    return [rawMessages];
  }

  return [];
};

const isCashRegisterSessionClosedError = (error: any): boolean => {
  const messages = [
    ...cashRegisterSessionErrorMessages(error),
    extractApiErrorMessage(error, ''),
  ].join(' ').toLowerCase();

  if (!messages) {
    return false;
  }

  return messages.includes('caisse ferm')
    || messages.includes('session de caisse ferm')
    || messages.includes('ouvrez une caisse')
    || messages.includes('pas autorisée pour la reprise')
    || messages.includes('aucune autorisation active');
};

const shouldHandleClosedCashRegisterSession = (error: any): boolean => {
  const user = currentAuthUser();

  return user?.role === 'caissier'
    && Boolean(currentCashRegisterSession())
    && isCashRegisterSessionClosedError(error);
};

const markClosedCashRegisterSessionHandled = (error: any): void => {
  if (error && typeof error === 'object') {
    error[CASH_REGISTER_SESSION_CLOSED_HANDLED_FLAG] = true;
  }
};

const handleClosedCashRegisterSession = async (): Promise<void> => {
  await store.dispatch('cashRegister/clear');

  if (isHandlingClosedCashRegisterSession) {
    return;
  }

  isHandlingClosedCashRegisterSession = true;

  showAppToast({
    type: 'warning',
    message: 'Votre session de caisse est fermée. Vous avez été déconnectée de cette caisse.',
    duration: 6500,
  });

  try {
    window.dispatchEvent(new CustomEvent(CASH_REGISTER_SESSION_CLOSED_EVENT));
  } finally {
    isHandlingClosedCashRegisterSession = false;
  }
};

api.interceptors.response.use(
  (response) => {
    const message = response.data?.message;
    const skipSuccessToast = Boolean((response.config as ToastAwareRequestConfig)?.skipSuccessToast);

    if (isMutationMethod(response.config?.method) && message && !skipSuccessToast) {
      showAppToast({
        type: 'success',
        message,
      });
    }

    return response;
  },
  (error) => {
    if (
      isUnauthenticatedError(error)
      && !isLoginRequest(error?.config)
      && requestHadAuthToken(error?.config)
    ) {
      void handleUnauthenticatedResponse();

      return Promise.reject(error);
    }

    if (shouldHandleClosedCashRegisterSession(error)) {
      markClosedCashRegisterSessionHandled(error);
      void handleClosedCashRegisterSession();

      return Promise.reject(error);
    }

    const skipErrorToast = Boolean((error.config as ToastAwareRequestConfig | undefined)?.skipErrorToast);
    const message = extractApiErrorMessage(error, 'Une erreur est survenue.');

    if (!skipErrorToast) {
      showAppToast({
        type: 'error',
        message,
        duration: 6500,
      });
    }

    return Promise.reject(error);
  }
);

export default api;
